/**
 * The system admin tour builder, as served by `/api/admin/tours`.
 *
 * Separate from `types/tours.ts` on purpose: that file is the *runtime* shape every coach's client
 * consumes, this one is the editing shape only a system admin ever sees. They share nothing but the
 * kind enum, and a change to one should not drag the other along.
 *
 * Every field the API might add later is optional at the boundary rather than here;
 * `services/admin-tours.ts` normalises the raw response, because web and API deploy separately and
 * a newer client can meet an older API (see the deploy-skew note in the root docs).
 */
import type { TourKind } from './tours';

export type AdminTourPlatform = 'Web' | 'Mobile';

/** Which platform's step list the editor is showing. Lower case: it is also the response key. */
export type AdminTourPlatformKey = 'web' | 'mobile';

export const PLATFORM_KEYS: readonly AdminTourPlatformKey[] = ['web', 'mobile'];

export const PLATFORM_LABELS: Record<AdminTourPlatformKey, string> = {
  web: 'Web',
  mobile: 'Mobile',
};

export const PLATFORM_VALUES: Record<AdminTourPlatformKey, AdminTourPlatform> = {
  web: 'Web',
  mobile: 'Mobile',
};

/** Code default or an admin's own step. Drives the badge, the revert, and the delete rules. */
export type AdminTourStepSource = 'Code' | 'Admin';

/** One element a step can point at, as the manifest lists it for a platform. */
export interface AdminTourAnchor {
  id: string;
  label: string;
  /** The route the element lives on, shown so two similar labels can be told apart. */
  route: string;
}

/** The copy limits the API validates against. Shown as a live count so a 400 is avoidable. */
export const TOUR_TITLE_MAX_LENGTH = 60;
export const TOUR_BODY_MAX_LENGTH = 280;

export interface AdminTourPlatformCounts {
  enabled: number;
  total: number;
}

export interface AdminTourSummary {
  key: string;
  kind: TourKind;
  name: string;
  webRoute: string | null;
  mobileRoute: string | null;
  isEnabled: boolean;
  hasDraft: boolean;
  resetAt: string | null;
  removedFromCode: boolean;
  counts: Record<AdminTourPlatformKey, AdminTourPlatformCounts>;
  newFromCodeCount: number;
  removedFromCodeCount: number;
}

export interface AdminTourFeature {
  key: string;
  name: string;
  tours: AdminTourSummary[];
}

export interface AdminToursResponse {
  features: AdminTourFeature[];
}

export interface AdminTourStep {
  key: string;
  platform: AdminTourPlatform;
  /** Null means a centered card with nothing highlighted. */
  anchorId: string | null;
  /** Null with an `anchorId` set means the app no longer carries that anchor. */
  anchorLabel: string | null;
  source: AdminTourStepSource;
  /** The copy in force: the override when there is one, otherwise the default. */
  title: string;
  body: string;
  defaultTitle: string;
  defaultBody: string;
  hasOverride: boolean;
  requiredPermission: string | null;
  sortOrder: number;
  isEnabled: boolean;
  isNewFromCode: boolean;
  removedFromCode: boolean;
}

export interface AdminTourDetail {
  key: string;
  feature: string;
  kind: TourKind;
  name: string;
  webRoute: string | null;
  mobileRoute: string | null;
  isEnabled: boolean;
  resetAt: string | null;
  /** The optimistic-concurrency token. Null when there is no draft. */
  draftUpdatedAt: string | null;
  draftUpdatedByName: string | null;
  hasDraft: boolean;
  steps: Record<AdminTourPlatformKey, AdminTourStep[]>;
  /**
   * Published steps this draft will delete on publish.
   *
   * The API omits them from `steps`, so the editor cannot infer the list by looking at what is
   * there. Read back so the next save re-sends them: `PUT {key}/draft` replaces the stored change
   * set, and a save that forgot a previous session's deletion would quietly resurrect that step.
   * Optional at the boundary, and empty when the API does not send it.
   */
  deletedStepKeys: string[];
}

/**
 * One step's edits. A per-step change set rather than the whole list, so a manifest change between
 * editing and publishing cannot drop or resurrect a step (see the plan's Phase 7).
 */
export interface AdminTourStepChange {
  isEnabled?: boolean;
  /** Null clears the override, which is how a step goes back to its code default copy. */
  titleOverride?: string | null;
  bodyOverride?: string | null;
  sortOrder?: number;
}

/**
 * A step the admin added that has never been published, identified by a client-generated id.
 *
 * It has no server key until publish, so it cannot ride in `steps`: the API keys that map by the
 * step key it already knows. `clientId` is what survives the round trip - the API hands the step
 * back inside `steps.<platform>` as `draft:<clientId>` so the editor re-renders it from the server
 * like any other step.
 */
export interface AdminTourNewStep {
  clientId: string;
  platform: AdminTourPlatform;
  /** Null means a centered card with nothing highlighted. */
  anchorId: string | null;
  title: string;
  body: string;
  sortOrder: number;
}

export interface AdminTourDraftRequest {
  expectedDraftUpdatedAt: string | null;
  tourEnabled: boolean;
  steps: Record<string, AdminTourStepChange>;
  newSteps: AdminTourNewStep[];
  /** Published admin steps to hard-delete on publish. Code steps are never named here. */
  deletedStepKeys: string[];
}

export interface AdminTourDraftState {
  draftUpdatedAt: string | null;
  hasDraft: boolean;
}

export interface AdminTourFieldError {
  field: string;
  message: string;
}

/** A step publish refused because its anchor is not in the manifest for its platform. */
export interface AdminTourBlockingStep {
  key: string;
  anchorId: string | null;
}

/**
 * The ways a write can fail that the editor renders rather than throws.
 *
 * - `conflict` (409): someone else saved first. Offers a reload.
 * - `validation` (400): copy the API refused. Rendered next to the step it belongs to.
 * - `blocked` (422): publish refused because a step points at an anchor the app no longer has.
 */
export type AdminTourWriteFailure =
  | { kind: 'conflict'; message: string }
  | { kind: 'validation'; message: string; errors: AdminTourFieldError[] }
  | { kind: 'blocked'; message: string; steps: AdminTourBlockingStep[] };

export type AdminTourWriteResult<T> =
  | { ok: true; value: T }
  | { ok: false; failure: AdminTourWriteFailure };

/**
 * The global switch served by `/api/admin/tours/settings`.
 *
 * While `enabledForEveryone` is false, `GET /api/tours/me` serves tours to system admins only, so
 * they can check them before coaches see anything. Null `updatedAt` means nobody has set it yet.
 */
export interface AdminTourSettings {
  enabledForEveryone: boolean;
  updatedAt: string | null;
}
